import { useMutation, useQuery, useQueryClient } from "react-query";

import { UserDto } from "@bcasmart/doxecure-commons/doxecureClientTypes";
import { UserProfile } from "@bcasmart/doxecure-commons/doxecureTypes";
import { $ok } from "foundation-ts/commons";
import { $password } from "foundation-ts/crypto";
import { TSError } from "foundation-ts/tserrors";
import { Nullable, UUID } from "foundation-ts/types";

import { UserParameters } from "@/components/UserInfos/UserInfos";
import { createCorporationUser, getCorporationUsers } from "@/services/corporations.service";
import { createAuthentication, deleteUser, patchUser, setAdminRole } from "@/services/users.service";
import { UsersID } from "./hooksConstants";
import { useAuthContext } from "./useAuthContext";

export const useUsers = () => {
    const { connectedUser, isAuthenticated } = useAuthContext();
    const queryClient = useQueryClient();

    const corporationApid = connectedUser?.corporations?.first()?.apid;

    const query = useQuery<Nullable<UserDto[]>>({
        queryKey: [UsersID, corporationApid],
        queryFn: () => getCorporationUsers(corporationApid),
        enabled: isAuthenticated && $ok(corporationApid),
        staleTime: Infinity,
    });

    const invalidate = () => queryClient.invalidateQueries([UsersID, corporationApid]);

    const addUser = useMutation({
        mutationFn: async (data: UserParameters) => {
            const userDto: UserDto = {
                firstName: data.firstName.capitalize(),
                lastName: data.lastName.toUpperCase(),
                email: data.email
            };
            const user = await createCorporationUser(corporationApid || data.corporationId!, userDto) ;
            if (!await createAuthentication(user.apid!, $password(12, { usesDigits: true, usesLowercase: true, usesUppercase: true, usesSpecials: true }))) {
                TSError.throw("Une erreur est survenue lors de la création du lien d'activation");
            }
        },
        onSuccess: invalidate,
    });

    const removeUser = useMutation({
        mutationFn: async (data: UserParameters) => {
            if (!await deleteUser(data.apid!)) {
                TSError.throw(`Erreur lors de la suppression définitive de ${data.firstName} ${data.lastName}`, 500);
            }
        },
        onSuccess: invalidate
    });

    const sendActivationLink = useMutation({
        mutationFn: async (data: UserParameters) => {
            if (data.userFlags?.profile === UserProfile.Contact) {
                const updatingUserDto: UserDto = {
                    apid: data.apid,
                    firstName: data.firstName.capitalize(),
                    lastName: data.lastName.toUpperCase(),
                    email: data.email,
                };
                await patchUser(updatingUserDto);
            }

            if (!await createAuthentication(data.apid!, $password(12, { usesDigits: true, usesLowercase: true, usesUppercase: true, usesSpecials: true }))) {
                TSError.throw("Une erreur est survenue lors de la création du lien d'activation", 500);
            }
        },
        onSuccess: invalidate
    });

    const setAdminCorporateRole = useMutation({
        mutationFn: async ({userId, toAdd}: {userId: UUID, toAdd: boolean}) => {
            await setAdminRole(userId, toAdd);
        },
        onSuccess: invalidate
    });

    return {
        ...query,
        addUser,
        refreshUsers: invalidate,
        removeUser,
        sendActivationLink,
        setAdminCorporateRole
    }
}